import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { getCurrentUser, signOut, selectTeam } from '../services/appwrite';
import { useTeam } from '../context/TeamContext';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const SelectOrganisation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedTeamId, clearTeam } = useTeam();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selecting, setSelecting] = useState(false);
  
  // Get the return URL from location state, default to dashboard
  const returnTo = location.state?.returnTo || '/dashboard';

  useEffect(() => {
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const currentUser = await getCurrentUser();
      
      if (!currentUser) {
        toast.error('Authentication failed. Please log in again.');
        navigate('/login', { replace: true });
        return;
      }

      const userTeams = currentUser.teams || [];

      if (userTeams.length === 0) {
        // Signed in, but not part of any shop yet. That's an unfinished signup,
        // so offer to finish it rather than logging them out.
        navigate('/register', { replace: true });
        return;
      }

      // If only one team, select it automatically
      if (userTeams.length === 1) {
        await handleSelectTeam(userTeams[0]);
        return;
      }

      setTeams(userTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTeam = async (team) => {
    try {
      setSelecting(true);

      // Store selected team in both appwrite service and TeamContext
      const teamId = team.$id || team.id; // Support both Appwrite format ($id) and our normalized format (id)
      selectTeam(teamId);
      setSelectedTeamId(teamId);

      // Navigate to the intended destination or dashboard
      toast.success(`Signed in to ${team.name}`);
      
      // Use window.location to ensure full reload with correct team context
      window.location.href = returnTo;
    } catch (error) {
      console.error('Error selecting team:', error);
      toast.error('Failed to select organisation. Please try again.');
    } finally {
      setSelecting(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
      <ThemeToggle />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Skeleton className="h-12 w-12 rounded-full" />
            </div>
            <CardTitle className="text-2xl">Loading organizations...</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="flex justify-center space-x-2">
              <Skeleton className="h-4 w-4 rounded-full animate-bounce" />
              <Skeleton className="h-4 w-4 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
              <Skeleton className="h-4 w-4 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
      <ThemeToggle />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Select Organisation</CardTitle>
          <p className="text-muted-foreground">Choose which organisation to sign in to</p>
        </CardHeader>

        <CardContent className="space-y-3">
          {teams.map((team) => (
            <Button
              key={team.id}
              onClick={() => handleSelectTeam(team)}
              className="w-full h-auto py-4 text-left justify-start"
              variant="outline"
              disabled={selecting}
            >
              <div className="flex flex-col items-start gap-1">
                <div className="font-semibold text-base">{team.name}</div>
              </div>
            </Button>
          ))}

          <div className="pt-4 border-t">
            <Button
              onClick={async () => {
                clearTeam(); // Clear team from context
                await signOut();
                navigate('/login', { replace: true });
              }}
              variant="ghost"
              className="w-full"
              disabled={selecting}
            >
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SelectOrganisation;